import type { CSSProperties } from "react";

/** Invoice/portal logos are JPG or PNG only (the backend enforces the same). */
export const JPG_PNG_ACCEPT = ["image/jpeg", "image/png"];

/**
 * Mirrors the backend contract in `common/invoice-logo-crop.ts`. The API
 * re-validates everything, so drift here is a rejected save, never a bad render.
 */
export const MIN_SIZE_PT = 40;
export const MAX_SIZE_PT = 160;
/** Pre-feature size; shown as 100% so the readout reads "same as before". */
export const DEFAULT_SIZE_PT = 76;
/** The renderer divides by the crop extent, so it must not approach zero. */
export const MIN_CROP_EXTENT_PCT = 1;

export interface CropFractions {
  /** 0..1 fractions of the source image's natural dimensions. */
  x: number;
  y: number;
  w: number;
  h: number;
  sizePt: number;
}

/**
 * CSS for a div that shows the cropped region scaled into a box, reproducing
 * what `invoice-preview-pdf.service` does with PDFKit clipping.
 *
 * Uses a scaled background image rather than a canvas so it is cheap enough to
 * recompute on every drag frame, and so the same helper works in both the crop
 * dialog and the settings card — the two must never disagree about what the
 * invoice will look like.
 *
 * `framePx` is the box representing the LARGEST allowed logo; the returned box
 * scales within it by sizePt, so the size control has a stable reference to
 * grow against. Returns null when the crop is degenerate or the image's aspect
 * ratio is not known yet.
 */
export function buildInvoiceLogoPreviewStyle(
  imageUrl: string,
  crop: CropFractions,
  naturalRatio: number | null,
  framePx: number
): CSSProperties | null {
  if (!naturalRatio || !Number.isFinite(naturalRatio)) return null;
  if (crop.w < MIN_CROP_EXTENT_PCT / 100 || crop.h < MIN_CROP_EXTENT_PCT / 100) return null;

  const cropAspect = (crop.w * naturalRatio) / crop.h;
  const sizeBox = framePx * (crop.sizePt / MAX_SIZE_PT);
  let boxW = sizeBox;
  let boxH = sizeBox;
  if (cropAspect >= 1) boxH = sizeBox / cropAspect;
  else boxW = sizeBox * cropAspect;

  return {
    width: `${boxW}px`,
    height: `${boxH}px`,
    backgroundImage: `url(${imageUrl})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: `${boxW / crop.w}px ${boxH / crop.h}px`,
    backgroundPosition: `-${crop.x * (boxW / crop.w)}px -${crop.y * (boxH / crop.h)}px`,
  };
}

/** Loads an image just to learn its aspect ratio, for the preview math. */
export function loadNaturalRatio(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth / img.naturalHeight);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
